import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  useTheme
} from "@mui/material";
import { CheckmarkCircle20Px, useToast } from "@qubit/autoparts";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { AutostoreBin } from "~/features/autostoreBin";

import { AlertInfo } from "~/features/inventory/components/AlertInfo";
import {
  mixpanelTrack,
  mixpanelTrackModalDismiss,
  useMixpanelModalName
} from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";

import {
  selectThisWorkstation,
  selectWorkstationAutostoreGridId
} from "~/redux/selectors/workstationsSelectors";
import { useReconfigureBinCompartmentsMutation } from "~/redux/warehouse/autostoreGrid.hooks";
import { useGetBinConfigurationsQuery } from "~/redux/warehouse/bin.hooks";
import { AutostoreBinConfigurationDto } from "~/types/api";

export function EscalateModal({
  open,
  onClose,
  onRelease,
  binConfiguration,
  binNumber
}: {
  open: boolean;
  onClose: () => void;
  onRelease: () => Promise<void>;
  binConfiguration: AutostoreBinConfigurationDto;
  binNumber: number;
}) {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const { errorToast, successToast } = useToast();
  const trackedModalName = useMixpanelModalName("Bin Escalation");

  const [reconfigureBinCompartments, { isLoading }] =
    useReconfigureBinCompartmentsMutation();
  const { data: availableBinConfigurations } = useGetBinConfigurationsQuery();

  const selectedWorkstation = useAppSelector(selectThisWorkstation);
  const autostoreGridId = useAppSelector(selectWorkstationAutostoreGridId);

  const [hasBeenReconfigured, setHasBeenReconfigured] = useState(false);

  const wholeBinConfiguration = availableBinConfigurations?.find(
    (config) => config.numberOfCompartments === 1
  );

  // Whole bins, don't need reconfiguration
  useEffect(() => {
    if (binConfiguration.numberOfCompartments === 1) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setHasBeenReconfigured(true);
    } else {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setHasBeenReconfigured(false);
    }
  }, [binConfiguration.numberOfCompartments]);

  const alertMessage = hasBeenReconfigured
    ? t("you are escalating to exterior maintenance. are you sure?")
    : t("please remove all bin dividers in order to escalate the bin");

  const handleReconfigureCompartments = async () => {
    if (!selectedWorkstation || !autostoreGridId || !wholeBinConfiguration) {
      return;
    }
    try {
      await reconfigureBinCompartments({
        autostoreGridId,
        binNumber,
        configurationId: wholeBinConfiguration.configurationId,
        workstationId: selectedWorkstation.id
      }).unwrap();

      successToast(`Successfully reconfigured bin to have 1 compartment`);
      setHasBeenReconfigured(true);
    } catch (err) {
      errorToast(getMessageFromRtkError(err), {
        trackingLocation: trackedModalName
      });
    }
  };

  const handleClose = () => {
    setHasBeenReconfigured(false);
    onClose();
  };

  const handleRelease = async () => {
    await onRelease();
    setHasBeenReconfigured(false);
  };

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      onClose={(_event, reason) => {
        mixpanelTrackModalDismiss(trackedModalName, reason, {
          "Bin Number": binNumber
        });
        handleClose();
      }}
    >
      <DialogTitle>{t("bin escalation")}</DialogTitle>
      <DialogContent sx={{ margin: "30px 30px 0" }}>
        <Stack direction="column" spacing={2} alignItems="center">
          <AlertInfo message={alertMessage} />
          <Box width="100%" justifyItems="center" position="relative">
            <AutostoreBin
              state="Bin Opened"
              pickQuantity={0}
              pickCompartment={null}
              numberOfRows={binConfiguration.horizontalCompartmentCount}
              numberOfColumns={binConfiguration.verticalCompartmentCount}
              selectedCompartment={hasBeenReconfigured ? 0 : undefined}
            />
            {hasBeenReconfigured && (
              <CheckmarkCircle20Px
                fill={palette.success.main}
                height="80px"
                width="80px"
                style={{
                  top: "calc(50% - 40px)",
                  right: "calc(50% - 40px)",
                  position: "absolute"
                }}
              />
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", p: 3 }}>
        <Button
          variant="subtle"
          size="large"
          onClick={() => {
            handleClose();
            mixpanelTrack({
              type: "Button Click",
              label: "Cancel",
              trackedPageName: trackedModalName,
              eventProperties: {
                "Bin Number": binNumber
              }
            });
          }}
        >
          {t("cancel")}
        </Button>
        <Button
          size="large"
          onClick={() => {
            mixpanelTrack({
              type: "Button Click",
              label: hasBeenReconfigured
                ? "Release Bin"
                : "Confirm Configuration",
              trackedPageName: trackedModalName,
              eventProperties: {
                "Bin Number": binNumber
              }
            });
            return hasBeenReconfigured
              ? handleRelease()
              : handleReconfigureCompartments();
          }}
          disabled={isLoading}
        >
          {hasBeenReconfigured ? t("release bin") : t("confirm configuration")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
